/**
 * Terminology — central German defaults for every overrideable UI key.
 *
 * The active industry (via /api/auth/me or /api/public/salon/{slug}) may override
 * a subset of these via its `terminology` map. Empty / missing keys fall back to
 * the defaults defined here.
 *
 * KEEP IN SYNC with `backend/industries.py::TERMINOLOGY_KEYS`.
 */

import { createContext, useContext, useMemo } from "react";

// German default labels for every key.
export const DEFAULT_TERMS = {
  // Core entity nouns
  salon: "Salon",
  salon_plural: "Salons",
  salon_owner: "Salon-Inhaber",
  customer: "Kunde",
  customer_plural: "Kunden",
  staff: "Mitarbeiter",
  staff_plural: "Mitarbeiter",
  stylist: "Stylist",
  service: "Leistung",
  service_plural: "Leistungen",
  service_category: "Kategorie",
  addon: "Zusatzleistung",
  addon_plural: "Zusatzleistungen",
  booking: "Termin",
  booking_plural: "Termine",
  appointment: "Termin",
  appointment_plural: "Termine",
  gallery: "Galerie",
  blog: "Blog",
  newsletter: "Newsletter",

  // Action verbs / CTAs
  book_now: "Jetzt buchen",
  book_appointment: "Termin buchen",
  request_appointment: "Termin anfragen",
  view_pricing: "Preisliste ansehen",

  // Section / page labels
  pricing: "Preisliste",
  price_list: "Preise",
  the_team: "Das Team",
  about_us: "Über uns",
  contact: "Kontakt",
  opening_hours: "Öffnungszeiten",
  imprint: "Impressum",
  privacy_policy: "Datenschutz",
  terms_conditions: "AGB",
  loyalty_program: "Treueprogramm",
  customer_portal: "Kundenbereich",
  account: "Mein Konto",

  // Booking flow
  select_service: "Leistung wählen",
  select_staff: "Mitarbeiter wählen",
  select_date: "Datum wählen",
  select_time: "Uhrzeit wählen",
  duration: "Dauer",
  price: "Preis",
  total: "Gesamt",
  notes: "Anmerkung",
  reminder_optin: "Termin-Erinnerung per E-Mail erhalten",

  // Email labels
  treatment: "Behandlung",
  session: "Sitzung",
};

// Helper: build a `t(key, fallback?)` function from a sparse override map
export function makeT(termsOverride) {
  const map = termsOverride || {};
  return (key, fallback) => {
    if (map[key]) return map[key];
    if (DEFAULT_TERMS[key]) return DEFAULT_TERMS[key];
    return fallback ?? key;
  };
}

// React context for public (non-authenticated) pages
const PublicTerminologyContext = createContext({ terms: {}, industry: null });

export function PublicTerminologyProvider({ terms, industry, children }) {
  const value = useMemo(() => ({ terms: terms || {}, industry: industry || null }), [terms, industry]);
  return (
    <PublicTerminologyContext.Provider value={value}>
      {children}
    </PublicTerminologyContext.Provider>
  );
}

// Public hook (use when you have a salon-scoped public page)
export function usePublicTerminology() {
  const { terms, industry } = useContext(PublicTerminologyContext);
  const t = useMemo(() => makeT(terms), [terms]);
  return { t, terms, industry };
}
